import { ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import React from "react";

interface ChevronButtonProps {
  /** Whether the folder is expanded */
  isExpanded: boolean;
  /** Whether the folder has children */
  hasChildren: boolean;
  /** Called when the button is clicked */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ChevronButton - Atom component
 *
 * Displays expand/collapse control for folder tree nodes.
 * Shows ChevronDown when expanded, ChevronRight when collapsed, empty spacer when no children.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <ChevronButton isExpanded={false} hasChildren={true} onClick={toggleExpand} />
 * <ChevronButton isExpanded={true} hasChildren={true} onClick={toggleExpand} />
 * <ChevronButton isExpanded={false} hasChildren={false} onClick={() => {}} />
 * ```
 */
export const ChevronButton = React.memo(function ChevronButton({
  isExpanded,
  hasChildren,
  onClick,
  className,
}: ChevronButtonProps) {
  if (!hasChildren) {
    return <div className={cn("h-6 w-6", className)} aria-hidden="true" />;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleClick}
      className={cn(
        "h-6 w-6 p-0 transition-all duration-200",
        "hover:bg-slate-100 hover:shadow-subtle",
        "active:scale-95",
        className
      )}
      aria-expanded={isExpanded}
      aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
    >
      <ChevronRight
        className={cn(
          "h-4 w-4 text-slate-500 transition-all duration-200",
          "group-hover:text-slate-700",
          isExpanded && "rotate-90"
        )}
        aria-hidden="true"
      />
    </Button>
  );
});
